import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { formatCurrency } from 'ggx-service/i18n/format-currency.service'
import { displayAlert } from 'ggx-componentlibrary/components/alerts/alerts.component'
import { tollwaysService } from 'ggx-src/van-booking/api/tollways.service'
import { Modal } from 'ggx-componentlibrary/components/modal/modal.component'
import { Button } from 'ggx-componentlibrary/components/button/button.component'
import {
  Body,
  SmallText,
} from 'ggx-componentlibrary/design/typography/typography.styles'
import { Checkbox } from 'ggx-componentlibrary/components/checkbox/checkbox.component.js'
import styled from 'styled-components'
import ChevronDownSVG from './chevron_s_down.svg'
import { COLOR } from 'ggx-componentlibrary/design/color/color.constants'

const CheckBoxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  cursor: pointer;
  &:last-child {
    margin-bottom: 0;
  }
`

const StyledCheckbox = styled(Checkbox)`
  pointer-events: none;
`

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${COLOR.BLUE_MEDIUM};
  margin-bottom: 16px;
  cursor: pointer;
  &:last-child {
    margin-bottom: 0;
  }
  gap: 8px;
  * {
    display: inline-block;
  }
  &:hover {
    text-decoration: underline;
  }
`

const tunnelNameMapping = {
  any: 'common__tunnel__no_preference',
  hung: 'common__tunnel__cross-harbour-tunnel',
  western: 'common__tunnel__western_harbour_crossing',
  eastern: 'common__tunnel__eastern_harbour_crossing',
  lion_rock: 'common__tunnel__lion_rock_tunnel',
  tai_lam: 'common__tunnel__tai_lam_tunnel',
  shing_mun: 'common__tunnel__shing_mun_tunnel',
  tates_cairn: 'common__tunnel__tates_cairn_tunnel',
  eagles_nest: 'common__tunnel__eagles_nest_tunnel',
  tko: 'common__tunnel__tseung_kwan_o_tunnel',
  aberdeen: 'common__tunnel__aberdeen_tunnel',
  ckb: 'common__tunnel__ckb_tunnel',
}

const MultipleTunnelModal = ({
  vehicleType,
  defaultValue,
  highlightOptions = [],
  onSubmit,
  onClose,
  isEditingTunnelPreference,
  overlayClass,
}) => {
  const [t] = useTranslation()

  const defaultOption = {
    value: 'any',
    label: t('common__tunnel__no_preference'),
    singleValueMessage: t('no_preference__details'),
    dropdownValueMessage: t('no_preference__details'),
  }

  const [availableOptions, setAvailableOptions] = useState([])

  useEffect(() => {
    ;(async () => {
      try {
        const { values, prices } = (await tollwaysService.getTollways())[
          vehicleType
        ]
        setAvailableOptions(
          Array.from(values, (_, i) => ({
            value: values[i],
            price: prices[i],
          })).slice(1)
        )
      } catch {
        displayAlert({ message: t('errors.generic'), type: 'error' })
      }
    })()
  }, [vehicleType])
  const [value, setValue] = useState(defaultValue || ['any'])
  const renderOption = useCallback(
    item => (
      <CheckBoxContainer
        key={item.value}
        onClick={e => {
          e.stopPropagation()
          setValue(prev => {
            if (prev.includes(item.value)) {
              const tmp = prev.filter(v => v !== item.value)
              return tmp.length === 0 ? ['any'] : tmp
            }
            return [...prev.filter(i => i !== 'any'), item.value]
          })
        }}
      >
        <StyledCheckbox value={value.includes(item.value)} />
        <div>
          <Body typographyStyles="mb-none">
            {t(tunnelNameMapping[item.value])} (
            {formatCurrency({ amount: item.price })})
          </Body>
        </div>
      </CheckBoxContainer>
    ),
    [value, t]
  )

  const [showAll, setShowAll] = useState(highlightOptions.length === 0)
  return (
    <Modal
      height="initial"
      maxHeight="610px"
      header={
        isEditingTunnelPreference
          ? t('common__tunnel_preference')
          : t('add_tunnel_pref__title')
      }
      footer={
        <Button
          buttonType="primary"
          size="small"
          onClick={() => onSubmit?.(value)}
        >
          {t('signup.button.confirm')}
        </Button>
      }
      onRequestClose={onClose}
      overlayClass={overlayClass}
    >
      <Body typographyStyles="mb-m">
        {isEditingTunnelPreference
          ? t('subtitle__tunnel_preference')
          : t('add_tunnel_pref__body')}
      </Body>
      <CheckBoxContainer onClick={() => setValue(['any'])}>
        <StyledCheckbox value={value.includes('any')} />
        <div>
          <Body typographyStyles="mb-none">
            {t('common__tunnel__no_preference')}
          </Body>
          <SmallText typographyStyles="mb-none">
            {t('no_preference__details')}
          </SmallText>
        </div>
      </CheckBoxContainer>

      {highlightOptions.map(option => {
        const item = availableOptions.find(item => item.value === option)
        return item ? renderOption(item) : null
      })}
      {!showAll ? (
        <LinkContainer onClick={() => setShowAll(true)}>
          <ChevronDownSVG />
          <Body typographyStyles="mb-none c-blue_medium">
            {t('other_tunnels')}
          </Body>
        </LinkContainer>
      ) : (
        availableOptions
          .filter(item => !highlightOptions.includes(item.value))
          .map(renderOption)
      )}
    </Modal>
  )
}

MultipleTunnelModal.propTypes = {
  vehicleType: PropTypes.string.isRequired,
  defaultValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  highlightOptions: PropTypes.arrayOf(PropTypes.string),
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
  isEditingTunnelPreference: PropTypes.bool,
  overlayClass: PropTypes.string,
}

export { MultipleTunnelModal }
